function modifiers(event) {
  return {
    ctrl: Boolean(event.ctrlKey || event.metaKey),
    shift: Boolean(event.shiftKey),
    alt: Boolean(event.altKey),
  };
}

/** Map only the documented shortcuts; unrelated modifier chords are ignored. */
export function shortcutFor(event) {
  const key = String(event.key ?? '');
  const lower = key.toLowerCase();
  const { ctrl, shift, alt } = modifiers(event);

  if (ctrl && !alt && lower === 'z') return shift ? 'redo' : 'undo';
  if (ctrl && !shift && !alt && lower === 'y') return 'redo';
  if (ctrl && !shift && !alt && lower === 'd') return 'duplicate';
  if (ctrl && !alt && lower === 'g') return shift ? 'ungroup' : 'group';
  if (ctrl && !shift && !alt && lower === 'a') return 'select-all';
  if (ctrl) return null;

  if (shift && !alt && lower === 'a') return 'add-palette';
  if (shift) return null;
  if (alt) return null;

  const plain = {
    q: 'select-tool',
    w: 'move-tool',
    e: 'rotate-tool',
    r: 'scale-tool',
    f: 'frame-selection',
    home: 'frame-all',
    delete: 'delete',
    backspace: 'delete',
    m: 'mirror',
    b: 'box-select',
    escape: 'cancel',
    tab: 'toggle-edit-mode',
    1: 'vertex-mode',
    2: 'edge-mode',
    3: 'face-mode',
    x: 'extrude-face',
    i: 'inset-face',
    s: 'scale-face',
  };
  return plain[lower] ?? null;
}

/** Inputs and contenteditable surfaces keep their native keyboard behavior. */
export function isEditableTarget(target) {
  const tag = String(target?.tagName ?? '').toUpperCase();
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return true;
  if (target?.isContentEditable) return true;
  return Boolean(
    target?.closest?.('input[type="color"]') || target?.closest?.('[contenteditable="true"]'),
  );
}

/** Install one window-local shortcut dispatcher. */
export function createShortcutManager({ target, actions = {}, isBlocked = () => false }) {
  function keydown(event) {
    if (event.defaultPrevented) return;
    if (isEditableTarget(event.target)) return;
    if (isBlocked(event)) return;
    const name = shortcutFor(event);
    const action = name ? actions[name] : null;
    if (typeof action !== 'function') return;
    event.preventDefault();
    action(event);
  }

  target.addEventListener('keydown', keydown);
  return {
    destroy() {
      target.removeEventListener('keydown', keydown);
    },
  };
}
